
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  CalendarCheck,
  Clock3,
  Video,
  MapPin,
  Building2,
  BriefcaseBusiness,
  ExternalLink,
  MessageSquare,
  RefreshCw,
  ClipboardList,
  AlertCircle,
  Wallet,
  Briefcase,
  GraduationCap,
  Hash,
} from "lucide-react";

import "./TrainerInterviews.css";

const API = "/api";

const getArray = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.content)) return response.content;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.applications)) return response.applications;
  return [];
};

const firstValue = (...values) =>
  values.find(
    (value) =>
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ""
  );

const formatDate = (date) => {
  if (!date) return "Not provided";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return String(date);

  return parsedDate.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (time) => {
  if (!time) return "Not provided";

  const match = String(time).match(/^(\d{1,2}):(\d{2})/);

  if (!match) return String(time);

  const date = new Date();
  date.setHours(Number(match[1]), Number(match[2]), 0, 0);

  return date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getJobDetails = (job, application) => ({
  title: firstValue(
    job?.jobTitle,
    job?.title,
    application?.jobTitle
  ),

  location: firstValue(
    job?.jobLocation,
    job?.location,
    application?.jobLocation
  ),

  employment: firstValue(
    job?.employmentType,
    job?.employment,
    job?.jobType,
    job?.type
  ),

  experience: firstValue(
    job?.experienceRequired,
    job?.requiredExperience,
    job?.experience
  ),

  budget: firstValue(
    job?.budget,
    job?.salaryRange,
    job?.salary,
    job?.compensation
  ),

  jobId: firstValue(job?.jobId, job?.id, application?.jobId),

  company: firstValue(
    job?.companyName,
    job?.company?.companyName,
    application?.companyName
  ),

  appliedOn: firstValue(
    application?.appliedAt,
    application?.appliedOn,
    application?.appliedDate,
    application?.applicationDate
  ),
});

const displayValue = (value) => {
  if (value === undefined || value === null || value === "") {
    return "Not provided";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

export default function TrainerInterviews() {
  const navigate = useNavigate();

  const [interviews, setInterviews] = useState([]);
  const [jobDetailsById, setJobDetailsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadInterviews = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      let trainerId =
        user.trainerId ||
        user.trainer?.id ||
        localStorage.getItem("trainerId");

      // Find trainer profile if the ID is not saved.
      if (!trainerId) {
        const trainerResponse = await fetch(`${API}/trainers/all`, {
          headers,
        });

        if (!trainerResponse.ok) {
          throw new Error("Unable to load trainer profiles.");
        }

        const trainerData = await trainerResponse.json();
        const trainers = getArray(trainerData);

        const email = (
          user.email ||
          user.user?.email ||
          ""
        )
          .trim()
          .toLowerCase();

        const trainer = trainers.find(
          (item) =>
            item.email?.trim().toLowerCase() === email
        );

        if (!trainer) {
          throw new Error(
            "Trainer profile not found. Please log in again."
          );
        }

        trainerId = trainer.id;
        localStorage.setItem("trainerId", String(trainerId));
      }

      // Load trainer applications.
      const response = await fetch(
        `${API}/job-applications/trainer/${trainerId}`,
        { headers }
      );

      if (!response.ok) {
        throw new Error("Unable to load your interview schedule.");
      }

      const data = await response.json();
      const applications = getArray(data);

      const scheduledInterviews = applications
        .filter((application) => {
          const status = String(
            application.applicationStatus ||
              application.status ||
              ""
          ).toUpperCase();

          return [
            "INTERVIEW",
            "INTERVIEW_SCHEDULED",
          ].includes(status);
        })
        .sort((a, b) => {
          const first = `${a.interviewDate || ""} ${
            a.interviewTime || ""
          }`;

          const second = `${b.interviewDate || ""} ${
            b.interviewTime || ""
          }`;

          return first.localeCompare(second);
        });

      setInterviews(scheduledInterviews);

      // Fetch each related job once.
      const uniqueJobIds = [
        ...new Set(
          scheduledInterviews
            .map((application) => application.jobId)
            .filter((id) => id !== undefined && id !== null)
        ),
      ];

      const jobResults = await Promise.all(
        uniqueJobIds.map(async (jobId) => {
          try {
            const jobResponse = await fetch(
              `${API}/jobs/${jobId}`,
              { headers }
            );

            if (!jobResponse.ok) {
              console.error(
                `Unable to load job ${jobId}:`,
                jobResponse.status
              );

              return [String(jobId), null];
            }

            const jobData = await jobResponse.json();

            // Support either a direct DTO or a wrapped response.
            const job =
              jobData?.data ||
              jobData?.job ||
              jobData;

            return [String(jobId), job];
          } catch (jobError) {
            console.error(
              `Error fetching job ${jobId}:`,
              jobError
            );

            return [String(jobId), null];
          }
        })
      );

      setJobDetailsById(Object.fromEntries(jobResults));
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInterviews();
  }, []);

  return (
    <main className="trainer-interviews-page">
      {/* Page heading */}
      <header className="interviews-header">
        <div className="interviews-heading-left">
          <button
            type="button"
            className="interviews-back-button"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} />
            <span>Back</span>
          </button>

          <div className="interviews-heading-icon">
            <CalendarDays size={27} strokeWidth={1.8} />
          </div>

          <div className="interviews-heading-text">
            <h1>My Interviews</h1>
            <p>
              Manage your interview schedule and invitation details.
            </p>
          </div>
        </div>

        <div className="interviews-header-actions">
          <div className="interview-count">
            <CalendarCheck size={17} />
            <span>
              {interviews.length}{" "}
              {interviews.length === 1
                ? "Interview"
                : "Interviews"}
            </span>
          </div>

          <button
            type="button"
            className="interviews-refresh-button"
            onClick={loadInterviews}
            disabled={loading}
            aria-label="Refresh interviews"
            title="Refresh interviews"
          >
            <RefreshCw
              size={17}
              className={loading ? "refresh-spinning" : ""}
            />
          </button>
        </div>
      </header>

      {/* Loading state */}
      {loading && (
        <section className="interviews-state-card">
          <div className="interviews-loading-icon">
            <RefreshCw
              size={29}
              className="refresh-spinning"
            />
          </div>

          <h2>Loading your interviews</h2>
          <p>
            We're retrieving your latest interview schedule.
          </p>
        </section>
      )}

      {/* Error state */}
      {!loading && error && (
        <section className="interviews-state-card interviews-error-state">
          <div className="interviews-state-icon error-icon">
            <AlertCircle size={30} />
          </div>

          <h2>Couldn't load interviews</h2>
          <p>{error}</p>

          <button
            type="button"
            className="interviews-primary-button"
            onClick={loadInterviews}
          >
            <RefreshCw size={16} />
            Try again
          </button>
        </section>
      )}

      {/* Empty state */}
      {!loading && !error && interviews.length === 0 && (
        <section className="interviews-state-card interviews-empty-state">
          <div className="interviews-empty-illustration">
            <CalendarDays size={39} strokeWidth={1.6} />
            <span className="empty-calendar-dot" />
          </div>

          <span className="interviews-eyebrow">
            YOUR INTERVIEW SPACE
          </span>

          <h2>No interviews scheduled yet</h2>

          <p className="interviews-empty-description">
            When a training partner schedules an interview for one
            of your applications, the date, time, meeting link and
            other invitation details will appear here.
          </p>

          <div className="interviews-empty-tip">
            <ClipboardList size={19} />
            <span>
              Keep track of your job applications while you wait
              for interview invitations.
            </span>
          </div>

          <button
            type="button"
            className="interviews-secondary-button"
            onClick={() => navigate("/trainer/applications")}
          >
            View my applications
            <ArrowLeft
              size={16}
              className="button-arrow-right"
            />
          </button>
        </section>
      )}

      {/* Interview cards */}
      {!loading && !error && interviews.length > 0 && (
        <section className="interviews-results">
          <div className="interviews-results-heading">
            <div>
              <h2>Upcoming interview schedule</h2>
              <p>
                Your applications with interview status.
              </p>
            </div>
          </div>

          <div className="interviews-list">
            {interviews.map((application) => {
              const job = getJobDetails(
                jobDetailsById[String(application.jobId)],
                application
              );

              const jobItems = [
                {
                  label: "Company",
                  value: job.company,
                  icon: Building2,
                },
                {
                  label: "Location",
                  value: job.location,
                  icon: MapPin,
                },
                {
                  label: "Employment",
                  value: job.employment,
                  icon: Briefcase,
                },
                {
                  label: "Experience",
                  value: job.experience,
                  icon: GraduationCap,
                },
                {
                  label: "Budget",
                  value: job.budget,
                  icon: Wallet,
                },
                {
                  label: "Job ID",
                  value: job.jobId,
                  icon: Hash,
                },
                
                {
                  label: "Applied On",
                  value: job.appliedOn
                    ? formatDate(job.appliedOn)
                    : null,
                  icon: CalendarDays,
                },
              ];

              return (
                <article
                  className="trainer-interview-card"
                  key={application.applicationId}
                >
                  {/* Interview heading */}
                  <div className="interview-card-top">
                    <div className="interview-job-icon">
                      <BriefcaseBusiness size={23} />
                    </div>

                    <div className="interview-job-heading">
                      <span className="interview-job-label">
                        INTERVIEW INVITATION
                      </span>

                      <h3>
                        {job.title ||
                          `Job #${application.jobId || "-"}`}
                      </h3>

                      <p className="interview-company">
                        <Building2 size={15} />
                        {job.company || "Company not provided"}
                      </p>
                    </div>

                    <span className="interview-status-badge">
                      <span className="interview-status-dot" />
                      Interview
                    </span>
                  </div>

                  {/* Related job details */}
                  <section className="interview-job-details">
                    <div className="interview-job-details-heading">
                      <BriefcaseBusiness size={18} />
                      <h4>Shortlisted Job Details</h4>
                    </div>

                    <div className="interview-job-info-grid">
                      {jobItems.map((item) => {
                        const Icon = item.icon;

                        return (
                          
<div
  className={`interview-job-info-item ${
    item.label === "Company" ? "company-highlight" : ""
  }`}
  key={item.label}
>
  <span className="interview-job-info-label">
    <Icon size={15} />
    {item.label}
  </span>

  <strong>{displayValue(item.value)}</strong>
</div>

                        );
                      })}
                    </div>
                  </section>

                  {/* Interview date, time, mode and location */}
                  <div className="interview-detail-grid">
                    <div className="interview-detail-item">
                      <span className="interview-detail-icon">
                        <CalendarDays size={18} />
                      </span>

                      <div>
                        <span className="interview-detail-label">
                          Interview date
                        </span>
                        <strong>
                          {formatDate(application.interviewDate)}
                        </strong>
                      </div>
                    </div>

                    <div className="interview-detail-item">
                      <span className="interview-detail-icon">
                        <Clock3 size={18} />
                      </span>

                      <div>
                        <span className="interview-detail-label">
                          Interview time
                        </span>
                        <strong>
                          {formatTime(application.interviewTime)}
                        </strong>
                      </div>
                    </div>

                    <div className="interview-detail-item">
                      <span className="interview-detail-icon">
                        <Video size={18} />
                      </span>

                      <div>
                        <span className="interview-detail-label">
                          Interview mode
                        </span>
                        <strong>
                          {application.interviewMode ||
                            "Not provided"}
                        </strong>
                      </div>
                    </div>

                    <div className="interview-detail-item">
                      <span className="interview-detail-icon">
                        <MapPin size={18} />
                      </span>

                      <div>
                        <span className="interview-detail-label">
                          Job location
                        </span>
                        <strong>
                          {application.jobLocation ||
                            "Not provided"}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* Meeting link */}
                  {application.meetingLink && (
                    <div className="interview-card-footer">
                      <div className="interview-link-description">
                        <Video size={17} />
                        <span>
                          Meeting link is available
                        </span>
                      </div>

                      <a
                        className="interviews-primary-button"
                        href={application.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Join interview
                        <ExternalLink size={16} />
                      </a>
                    </div>
                  )}

                  {/* Interview venue */}
                  {application.interviewLocation && (
                    <div className="interview-extra-detail">
                      <MapPin size={17} />
                      <div>
                        <strong>Interview venue</strong>
                        <p>{application.interviewLocation}</p>
                      </div>
                    </div>
                  )}

                  {/* Partner message */}
                  {application.interviewRemarks && (
                    <div className="interview-message">
                      <div className="interview-message-heading">
                        <MessageSquare size={17} />
                        <strong>
                          Message from the partner
                        </strong>
                      </div>

                      <p>{application.interviewRemarks}</p>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
}
